/**
 * Permission probes for camera / mic / location. Uses the Permissions
 * API where it exists, falls back to a best-effort `getUserMedia` /
 * `getCurrentPosition` attempt otherwise.
 *
 * The one-shot prompts here are explicitly user-gesture-driven — the
 * onboarding route calls them from button clicks so the OS lifts its
 * "user activation" requirement.
 */

export type PermState = 'unknown' | 'prompt' | 'granted' | 'denied' | 'unavailable';

async function queryPermission(name: PermissionName): Promise<PermState> {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
    return 'unknown';
  }
  try {
    const res = await navigator.permissions.query({ name });
    return res.state as PermState;
  } catch {
    return 'unknown';
  }
}

export async function probeCamera(): Promise<PermState> {
  if (!navigator.mediaDevices?.getUserMedia) return 'unavailable';
  return queryPermission('camera' as PermissionName);
}

export async function probeMic(): Promise<PermState> {
  if (!navigator.mediaDevices?.getUserMedia) return 'unavailable';
  return queryPermission('microphone' as PermissionName);
}

export async function probeGeo(): Promise<PermState> {
  if (!('geolocation' in navigator)) return 'unavailable';
  return queryPermission('geolocation');
}

/** Prompt for camera access — opens then immediately closes the stream. */
export async function requestCamera(): Promise<PermState> {
  if (!navigator.mediaDevices?.getUserMedia) return 'unavailable';
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: 'environment' } },
      audio: false,
    });
    for (const t of stream.getTracks()) t.stop();
    return 'granted';
  } catch (err) {
    const msg = err instanceof Error ? err.message.toLowerCase() : '';
    return msg.includes('notallowed') || msg.includes('denied') ? 'denied' : 'prompt';
  }
}

export async function requestMic(): Promise<PermState> {
  if (!navigator.mediaDevices?.getUserMedia) return 'unavailable';
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    for (const t of stream.getTracks()) t.stop();
    return 'granted';
  } catch (err) {
    const msg = err instanceof Error ? err.message.toLowerCase() : '';
    return msg.includes('notallowed') || msg.includes('denied') ? 'denied' : 'prompt';
  }
}

export function requestGeo(): Promise<PermState> {
  if (!('geolocation' in navigator)) return Promise.resolve('unavailable');
  return new Promise<PermState>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      () => resolve('granted'),
      (err) => resolve(err.code === err.PERMISSION_DENIED ? 'denied' : 'prompt'),
      { timeout: 8000, maximumAge: 60_000 },
    );
  });
}

/**
 * Durable on-device storage.
 *
 * This is the one "permission" that guards the product's whole premise. OPFS
 * on a *non-persisted* origin is best-effort: the browser may evict it under
 * storage pressure, and Safari additionally clears site data for origins that
 * go unused for a period. Every unsynced capture, and the SQLite database
 * holding the session itself, lives there — so an eviction is not a cache miss,
 * it is a lost inspection.
 *
 * `navigator.storage.persist()` is what moves the origin out of that category.
 * It behaves differently per engine and both behaviours are wanted:
 *
 *   * Chrome/Android grant it silently on a heuristic (installed PWA, high
 *     engagement) and never prompt — so the boot-time call in `main.tsx` is
 *     free and is where most users get it.
 *   * Firefox prompts — which is why it is also offered from a button in
 *     onboarding, where a prompt is expected rather than ambient.
 *
 * A denial is survivable, not fatal: capture still works, and the honest
 * consequence is that the inspector should sync sooner. Nothing here throws
 * upward.
 */
export async function probeStorage(): Promise<PermState> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persisted) return 'unavailable';
  try {
    return (await navigator.storage.persisted()) ? 'granted' : 'prompt';
  } catch {
    // `'unavailable'`, not `'unknown'`. The onboarding screen gates its
    // Continue button on every row having resolved to something other than
    // `'unknown'`, so returning that here would strand anyone whose browser
    // throws — a private window, a locked-down profile — on a screen they
    // cannot leave.
    return 'unavailable';
  }
}

/**
 * Ask for it, unless it is already held.
 *
 * Checking `persisted()` first matters on Firefox: re-requesting an origin
 * that already has persistence would put a prompt in front of someone who
 * already said yes.
 */
export async function requestPersistentStorage(): Promise<PermState> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) return 'unavailable';
  try {
    if (navigator.storage.persisted && (await navigator.storage.persisted())) return 'granted';
    return (await navigator.storage.persist()) ? 'granted' : 'denied';
  } catch {
    // Private windows and locked-down configurations throw rather than
    // returning false. Indistinguishable from a denial, and treated as one —
    // which also keeps the onboarding gate resolvable, since it refuses to let
    // anyone past a row still reading `'unknown'`.
    return 'denied';
  }
}

const PERSIST_ASKED_FLAG = 'kapture.storagePersistAsked';

/**
 * The boot-time call: ask at most once, ever.
 *
 * Requesting on every launch is wrong in both directions. On Chrome it is
 * free — the answer is a silent heuristic — but on the engines that prompt it
 * would put a permission dialog in front of someone on every cold start, and
 * the module docstring above says onboarding exists precisely so the prompt
 * arrives somewhere it is expected. Asking once ambiently and never again is
 * the compromise: an inspector who declines is not nagged, and the explicit
 * button in onboarding is still there for anyone who changes their mind.
 *
 * `persisted()` is checked first, so a device that already has persistence
 * never reaches the request at all and the flag is irrelevant to it.
 */
export async function ensurePersistentStorage(): Promise<PermState> {
  const already = await probeStorage();
  if (already !== 'prompt') return already;
  try {
    if (localStorage.getItem(PERSIST_ASKED_FLAG) === '1') return 'prompt';
    localStorage.setItem(PERSIST_ASKED_FLAG, '1');
  } catch {
    // No localStorage means no memory of having asked, and asking on every
    // launch is worse than not asking here at all — onboarding still offers it.
    return 'prompt';
  }
  return requestPersistentStorage();
}

export const ONBOARDING_FLAG = 'kapture.hasOnboarded';

export function markOnboarded(): void {
  try {
    localStorage.setItem(ONBOARDING_FLAG, '1');
  } catch {
    // localStorage can throw in private modes; no-op is fine
  }
}

export function hasOnboarded(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_FLAG) === '1';
  } catch {
    return true; // err on the side of not blocking the flow
  }
}
